import {
  Business,
  MedicationLiquid,
  PersonOutline,
} from "@mui/icons-material";
import {
  Box,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { useMemo, useState } from "react";

import { DocumentDateBadge } from "./DocumentDateBadge";
import { extractTimeFromResource } from "./dateBadgeUtils";
import { ExpandableStatusChip } from "./ExpandableStatusChip";
import { PatientPrescriptionCardActions } from "./PatientPrescriptionCardActions";
import type { PosologyLine } from "@/utils/fhirDocumentUtils";
import {
  extractOrganizationName,
  extractPosologyLines,
  findRelatedResources,
} from "@/utils/fhirDocumentUtils";
import {
  formatPractitionerLine,
  parseMedicationDescription,
  sanitizeSpecialty,
} from "./prescriptionStackTheme";

export interface PrescriptionStackItem {
  id?: number | string;
  description?: string;
  date?: string;
  practitioner?: string;
  specialty?: string;
  sign?: boolean | string;
  binaryUrl?: string;
  fullResource?: any;
  organization?: string | null;
  posologyLines?: PosologyLine[];
}

const CARD_BORDER = {
  borderRadius: 2.5,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
};

function isSignedItem(item: PrescriptionStackItem): boolean {
  if (typeof item.sign === "boolean") return item.sign;
  if (typeof item.sign === "string") {
    return item.sign.toLowerCase() === "sim" || item.sign === "true";
  }
  return false;
}

function resolveCardDetails(
  item: PrescriptionStackItem,
  medicationRequest?: any[],
) {
  const related = findRelatedResources(
    item.fullResource,
    medicationRequest ?? [],
  );
  const organization =
    item.organization ?? extractOrganizationName(item.fullResource);
  const posologyLines =
    item.posologyLines?.length && item.posologyLines.length > 0
      ? item.posologyLines
      : extractPosologyLines(related);

  return { organization, posologyLines, relatedCount: related.length };
}

function PrescriptionCardBack({
  organization,
  posologyLines,
  hasDateBadge,
}: {
  organization?: string | null;
  posologyLines?: PosologyLine[];
  hasDateBadge?: boolean;
}) {
  return (
    <Box
      sx={{
        ...CARD_BORDER,
        flex: 1,
        minWidth: 0,
        height: "100%",
        boxSizing: "border-box",
        p: 1.75,
        bgcolor: "grey.50",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        borderLeft: hasDateBadge ? 0 : undefined,
        borderTopLeftRadius: hasDateBadge ? 0 : CARD_BORDER.borderRadius,
        borderBottomLeftRadius: hasDateBadge ? 0 : CARD_BORDER.borderRadius,
      }}
    >
      <Stack
        spacing={1.25}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          pr: 0.25,
        }}
      >
        <Box>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mb: 0.5 }}
          >
            <Business sx={{ fontSize: 16, color: "primary.main" }} />
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "text.secondary",
              }}
            >
              ORGANIZAÇÃO
            </Typography>
          </Stack>
          <Typography
            sx={{
              fontSize: "0.85rem",
              lineHeight: 1.4,
              pl: 2.5,
              wordBreak: "break-word",
            }}
          >
            {organization || "Não informada"}
          </Typography>
        </Box>

        <Box>
          <Stack
            direction="row"
            spacing={0.75}
            alignItems="center"
            sx={{ mb: 0.75 }}
          >
            <MedicationLiquid sx={{ fontSize: 16, color: "primary.main" }} />
            <Typography
              sx={{
                fontSize: "0.72rem",
                fontWeight: 700,
                color: "text.secondary",
              }}
            >
              POSOLOGIA
            </Typography>
          </Stack>

          {posologyLines?.length ? (
            <Stack spacing={1} sx={{ pl: 0.5 }}>
              {posologyLines.map((line, index) => (
                <Box
                  key={`${line.name}-${index}`}
                  sx={{
                    pl: 2,
                    borderLeft: "2px solid",
                    borderColor: "primary.light",
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      lineHeight: 1.3,
                      wordBreak: "break-word",
                    }}
                  >
                    {line.name}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.78rem",
                      color: "text.secondary",
                      lineHeight: 1.45,
                      mt: 0.25,
                      wordBreak: "break-word",
                    }}
                  >
                    {line.posology}
                  </Typography>
                </Box>
              ))}
            </Stack>
          ) : (
            <Typography
              sx={{ fontSize: "0.8rem", color: "text.secondary", pl: 2.5 }}
            >
              Posologia não informada
            </Typography>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

interface PrescriptionMobileCardProps {
  item: PrescriptionStackItem;
  onView?: () => void;
  onSetBinaryUrl?: (url: string) => void;
  renderActions?: (item: PrescriptionStackItem) => React.ReactNode;
  medicationRequest?: any[];
}

function PrescriptionMobileCard({
  item,
  onView,
  onSetBinaryUrl,
  renderActions,
  medicationRequest,
}: PrescriptionMobileCardProps) {
  const theme = useTheme();
  const [flipped, setFlipped] = useState(false);
  const signed = isSignedItem(item);
  const { title, subtitle } = parseMedicationDescription(item.description);
  const practitionerLine = formatPractitionerLine(
    item.practitioner,
    sanitizeSpecialty(item.specialty),
  );
  const time = extractTimeFromResource(item.fullResource);
  const badgeColor = signed
    ? theme.palette.primary.main
    : theme.palette.warning.dark;

  const { organization, posologyLines } = useMemo(
    () => resolveCardDetails(item, medicationRequest),
    [item, medicationRequest],
  );

  const toggleFlip = () => setFlipped((prev) => !prev);

  const faceShellSx = {
    display: "flex",
    width: "100%",
    height: "100%",
    backfaceVisibility: "hidden" as const,
    WebkitBackfaceVisibility: "hidden" as const,
  };

  const cardRowSx = {
    display: "flex",
    flexDirection: "row" as const,
    width: "100%",
    height: "100%",
    alignItems: "stretch",
  };

  return (
    <Box sx={{ perspective: "1000px", width: "100%" }}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          minHeight: 148,
          transformStyle: "preserve-3d",
          transition: "transform 0.5s ease-in-out",
          transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        }}
      >
        {/* Frente */}
        <Box
          onClick={toggleFlip}
          sx={{
            ...faceShellSx,
            transform: "rotateY(0deg)",
            pointerEvents: flipped ? "none" : "auto",
            cursor: "pointer",
          }}
        >
          <Box sx={cardRowSx}>
            {item.date && (
              <DocumentDateBadge
                date={item.date}
                time={time}
                accentColor={badgeColor}
              />
            )}
            <Box
              sx={{
                ...CARD_BORDER,
                flex: 1,
                minWidth: 0,
                p: 1.75,
                borderLeft: item.date ? 0 : undefined,
                borderTopLeftRadius: item.date ? 0 : CARD_BORDER.borderRadius,
                borderBottomLeftRadius: item.date ? 0 : CARD_BORDER.borderRadius,
              }}
            >
              <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                gap={0.5}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: "0.9rem",
                    lineHeight: 1.35,
                    wordBreak: "break-word",
                    flex: 1,
                  }}
                >
                  {title}
                </Typography>
                <ExpandableStatusChip signed={signed} />
              </Stack>

              {subtitle && (
                <Typography
                  sx={{
                    mt: 0.35,
                    fontSize: "0.8rem",
                    color: "text.secondary",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: 1.4,
                  }}
                >
                  {subtitle}
                </Typography>
              )}

              {practitionerLine && (
                <Stack
                  direction="row"
                  spacing={0.5}
                  alignItems="flex-start"
                  sx={{ mt: 1 }}
                >
                  <PersonOutline
                    sx={{ fontSize: 15, color: "text.disabled", mt: 0.1 }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.75rem",
                      color: "text.secondary",
                      lineHeight: 1.4,
                    }}
                  >
                    {practitionerLine}
                  </Typography>
                </Stack>
              )}

              <Box
                sx={{ mt: 1.5 }}
                onClick={(e) => e.stopPropagation()}
              >
                {renderActions ? (
                  renderActions(item)
                ) : (
                  <PatientPrescriptionCardActions
                    row={item}
                    handleOpen={onView}
                    setBinaryUrl={onSetBinaryUrl}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Verso — mesma estrutura da frente (badge + painel) */}
        <Box
          onClick={toggleFlip}
          sx={{
            ...faceShellSx,
            position: "absolute",
            inset: 0,
            transform: "rotateY(180deg)",
            pointerEvents: flipped ? "auto" : "none",
            cursor: "pointer",
          }}
        >
          <Box sx={cardRowSx}>
            {item.date && (
              <DocumentDateBadge
                date={item.date}
                time={time}
                accentColor={badgeColor}
              />
            )}
            <PrescriptionCardBack
              organization={organization}
              posologyLines={posologyLines}
              hasDateBadge={Boolean(item.date)}
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

interface PrescriptionMobileListProps {
  items: PrescriptionStackItem[];
  renderActions?: (item: PrescriptionStackItem) => React.ReactNode;
  handleOpen?: () => void;
  setBinaryUrl?: (url: string) => void;
  medicationRequest?: any[];
}

export function PrescriptionMobileList({
  items,
  renderActions,
  handleOpen,
  setBinaryUrl,
  medicationRequest,
}: PrescriptionMobileListProps) {
  return (
    <Stack spacing={1.5} sx={{ width: "100%" }}>
      {items.map((item, index) => (
        <PrescriptionMobileCard
          key={item.id ?? index}
          item={item}
          onView={handleOpen}
          onSetBinaryUrl={setBinaryUrl}
          renderActions={renderActions}
          medicationRequest={medicationRequest}
        />
      ))}
    </Stack>
  );
}

export function PrescriptionStackCarousel(props: PrescriptionMobileListProps) {
  return <PrescriptionMobileList {...props} />;
}
